import { Link } from 'react-router-dom'
import { Moon, Sun, Github, Menu, User, LogOut, LogIn, Shield } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import AuthModal from '../ui/AuthModal'

export default function Header({ darkMode, toggleDarkMode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const { isAuthenticated, user, signOut, isAdmin } = useAuth()

  return (
  <header className="bg-white/80 dark:bg-gray-900 dark:border-gray-700 backdrop-blur-lg border-b border-nitrr-border sticky top-0 z-50">
      <nav className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4 max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform">
              <img 
                src="/icons/nitrr.svg" 
                alt="NIT Raipur Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-nitrr-text group-hover:text-nitrr-primary transition-colors">
                NITRR Logo Library
              </h1>
              <p className="text-xs text-nitrr-text-muted">
                Official Repository
              </p>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/all-logos" 
              className="text-nitrr-text-secondary hover:text-nitrr-primary font-medium transition-colors"
            >
              All Logos
            </Link>
            <Link 
              to="/browse" 
              className="text-nitrr-text-secondary hover:text-nitrr-primary font-medium transition-colors"
            >
              Categories
            </Link>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* User Menu or Sign In */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-nitrr-blue/10 hover:bg-nitrr-blue/20 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-nitrr-blue flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {user?.name}
                    </p>
                    {isAdmin && (
                      <p className="text-xs text-nitrr-blue font-medium">Admin</p>
                    )}
                  </div>
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                    </div>
                    
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    )}
                    
                    <button
                      onClick={() => {
                        signOut();
                        setShowUserMenu(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-nitrr-blue hover:bg-nitrr-blue-dark text-white font-medium rounded-lg transition-colors"
              >
                <LogIn size={18} />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}

            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-icon hidden md:flex items-center justify-center rounded-full bg-white dark:bg-gray-900 border border-nitrr-border w-9 h-9 p-1"
              aria-label="GitHub"
            >
              <img 
                src="/icons/github-mark.svg" 
                alt="GitHub" 
                className="w-7 h-7 object-contain mx-auto"
              />
            </a>
            
            <button
              onClick={toggleDarkMode}
              className="btn-icon"
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="btn-icon md:hidden"
              aria-label="Menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-nitrr-border bg-white/95 dark:bg-gray-900 backdrop-blur-sm absolute left-0 right-0 top-full">
            <div className="container mx-auto px-4 py-4 space-y-4">
              {isAuthenticated && (
                <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email}
                  </p>
                </div>
              )}
              
              <Link 
                to="/all-logos" 
                className="block text-nitrr-text-secondary hover:text-nitrr-primary font-medium transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                All Logos
              </Link>
              <Link 
                to="/browse" 
                className="block text-nitrr-text-secondary hover:text-nitrr-primary font-medium transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Categories
              </Link>
              
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="flex items-center gap-2 text-nitrr-blue font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Shield size={18} />
                  Admin Panel
                </Link>
              )}
              
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium"
                >
                  <LogOut size={18} />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 text-nitrr-blue font-medium"
                >
                  <LogIn size={18} />
                  Sign In
                </button>
              )}
              
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-nitrr-text-secondary hover:text-nitrr-primary font-medium transition-colors"
              >
                <img src="/icons/github.svg" alt="" className="w-5 h-5" />
                GitHub
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signin"
      />
    </header>
  )
}
